type FocusSiblingOptions = {
  current: HTMLElement;
  direction: "horizontal" | "vertical";
  elements: HTMLElement[];
};

type GetFocusableElementsOptions = {
  element?: HTMLElement;
  selector?: string;
};

const focusableSelector = [
  "a[href]",
  "button",
  "input",
  "textarea",
  "select",
  "details",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

const siblingOffsets: Record<
  FocusSiblingOptions["direction"],
  Partial<Record<string, number>>
> = {
  horizontal: { ArrowLeft: -1, ArrowRight: 1 },
  vertical: { ArrowDown: 1, ArrowUp: -1 },
};

export function findPreviousFocusable(el: HTMLElement): HTMLElement | null {
  const allFocusable = getFocusableElements();
  const index = allFocusable.indexOf(el);
  return index > 0 ? allFocusable[index - 1] : null;
}

export function focusSibling(
  event: Pick<KeyboardEvent, "key" | "preventDefault">,
  { current, direction, elements }: FocusSiblingOptions,
) {
  const offset = siblingOffsets[direction][event.key];
  if (!offset) return false;

  const index = elements.indexOf(current);
  if (index === -1) return false;

  const sibling = elements[index + offset];
  if (!sibling) return false;

  event.preventDefault();
  sibling.focus();
  return true;
}

export function getFocusableElements({
  element,
  selector = focusableSelector,
}: GetFocusableElementsOptions = {}) {
  return Array.from(
    (element ?? document).querySelectorAll<HTMLElement>(selector),
  ).filter(isFocusable);
}

export function hasModifierKey(
  event: Pick<
    KeyboardEvent,
    "altKey" | "ctrlKey" | "defaultPrevented" | "metaKey" | "shiftKey"
  >,
) {
  return (
    event.defaultPrevented ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey
  );
}

export function isNavigableTarget(
  target: EventTarget | null,
): target is HTMLElement {
  return target instanceof HTMLElement && !isTextEditable(target);
}

function isFocusable(element: HTMLElement) {
  return (
    !element.hasAttribute("disabled") &&
    !element.closest("[inert]") &&
    element.tabIndex >= 0 &&
    isVisible(element)
  );
}

function isTextEditable(element: HTMLElement) {
  return (
    element.isContentEditable ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    (element instanceof HTMLInputElement &&
      !nonTextInputTypes.includes(element.type))
  );
}

const nonTextInputTypes = ["button", "checkbox", "radio", "reset", "submit"];

function isVisible(element: HTMLElement) {
  const style = window.getComputedStyle(element);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    element.offsetParent !== null
  );
}
